"use client";

import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { charityQueryKeys } from "./charity-query-keys";
import {
  getAdminCharities,
  getAdminCharity,
  getAdminCharityProjects,
  getFieldsOfWork,
  getCertificates,
  type ListCharitiesParams,
  type ListCharitiesResponse,
  type GetCharityResponse,
  type ListProjectsResponse,
  type GetFieldsOfWorkResponse,
  type CertificateAdminDto,
} from "@/app/admin/charities/_api/charity-reads";
import { CharityStatus } from "@/openapi/generated";

export function useAdminCharities(
  params: {
    searchTerm?: string;
    status?: CharityStatus[];
    page?: number;
    size?: number;
    sort?: string[];
  },
  options?: Omit<UseQueryOptions<ListCharitiesResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: charityQueryKeys.list(params),
    queryFn: () =>
      getAdminCharities({
        searchTerm: params.searchTerm,
        status: params.status,
        page: params.page,
        size: params.size,
        sort: params.sort,
      } as ListCharitiesParams),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useAdminCharity(
  id: string,
  options?: Omit<UseQueryOptions<GetCharityResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: charityQueryKeys.detail(id),
    queryFn: () => getAdminCharity(id),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(id),
    ...options,
  });
}

export function useAdminCharityProjects(
  charityId: string,
  options?: Omit<UseQueryOptions<ListProjectsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: charityQueryKeys.projects(charityId),
    queryFn: () => getAdminCharityProjects(charityId),
    staleTime: 2 * 60 * 1000,
    enabled: Boolean(charityId),
    ...options,
  });
}

export function useFieldsOfWork(
  options?: Omit<UseQueryOptions<GetFieldsOfWorkResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: charityQueryKeys.fieldsOfWork,
    queryFn: () => getFieldsOfWork(),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

export function useCertificates(
  options?: Omit<UseQueryOptions<CertificateAdminDto[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: charityQueryKeys.certificates,
    queryFn: () => getCertificates(),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}
